import { useCallback } from 'react'
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import { Link } from 'react-router-dom'
import { EditTwoIcon, SyncErrorIcon } from '@/components/icons'
import CharacterPlus from '@/components/icons/CharacterPlus'
import { RenderIf } from '@/components/common'
import HydraulicAckButton from '@/features/config/components/common/HydraulicAckButton'
import DeleteIconButton from '@/features/config/components/common/DeleteIconButton'
import { CONFIG_ROUTES } from '@/constant/route'
import type { GridCellCtx } from '@/components/grid/types'
import {
  SensorGlyph,
  ValveGlyph,
} from '../../HydraulicRule/FlowRulePanel/EmptyList'
import { DANGER_TEXT } from '../../HydraulicRule/FlowRulePanel/constants'
import { ACK_NO_VALVE_HINT_KEY, MAX_PIPE_LEVEL } from './constants'
import { brandLinkChip } from './cell-styles'
import { AddRowChip } from './chips'
import { CLEAR_COPY, flowRuleStateMeta } from './flow-rule-state'
import { useRuleCopy } from '../../HydraulicRule/FlowRulePanel/use-rule-copy'
import { isDraftId } from './tree-data'
import type { HydraulicTreeRow } from './types'
import type { SectionPopoverState } from './use-flow-rule-actions'

interface UseCellRenderersArgs {
  isDark: boolean
  isViewOnly: boolean
  bulkEditMode: boolean
  t: (key: string, options?: any) => string
  convertFromMetricV2: (
    value: number,
    from: string,
    to: string,
    precision?: number,
  ) => any
  // Flow rule
  creatingRuleFor: number | null
  ruleActiveMap: Record<string, boolean>
  sectionPopover: SectionPopoverState | null
  handleCreateRule: (row: HydraulicTreeRow) => void
  handleRequestDeleteRule: (row: HydraulicTreeRow) => void
  handleOpenAlarm: (row: HydraulicTreeRow, anchorEl: HTMLElement | null) => void
  handleOpenSection: (
    row: HydraulicTreeRow,
    section: Exclude<SectionPopoverState['section'], 'alarm'>,
    anchorEl: HTMLElement | null,
  ) => void
  /** Flow rules with at least one faulted valve or pump. */
  rulesWithValveError: ReadonlySet<number>
  // Row actions
  handleAcknowledge: (row: HydraulicTreeRow) => void
  /** False when the pipe's rule has no flow valve to reset through. */
  canAcknowledge: (row: HydraulicTreeRow) => boolean
  /** Pipes whose clear is in flight and not yet confirmed. */
  clearingIds: ReadonlySet<number>
  /** Pipes whose error returned after a clear. */
  returnedIds: ReadonlySet<number>
  dismissReturned: (pipeId: number) => void
  handleAddSubPipe: (id: string) => void
  handleEdit: (id: string, parentId: string) => void
  handleDelete: (
    id: number,
    interfaceId: number,
    flowRuleId?: number,
    name?: string,
  ) => void
  handleDiscardDraft: (id: number) => void
  addDraft: (
    parent: { id: number; level: number; interfaceId: number } | null,
  ) => void
}

/**
 * The grid's cell renderers.
 *
 * Each keeps its own `useCallback` and dependency list rather than sharing one
 * memo: the description renderer feeds the tree's group column, and rebuilding
 * that column makes the grid rebuild its whole row model.
 */
const useCellRenderers = ({
  isDark,
  isViewOnly,
  bulkEditMode,
  t,
  convertFromMetricV2,
  creatingRuleFor,
  ruleActiveMap,
  sectionPopover,
  handleCreateRule,
  handleRequestDeleteRule,
  handleOpenAlarm,
  handleOpenSection,
  rulesWithValveError,
  handleAcknowledge,
  canAcknowledge,
  clearingIds,
  returnedIds,
  dismissReturned,
  handleAddSubPipe,
  handleEdit,
  handleDelete,
  handleDiscardDraft,
  addDraft,
}: UseCellRenderersArgs) => {
  const copy = useRuleCopy()
  const RuleCell = useCallback(
    ({ row }: GridCellCtx<HydraulicTreeRow>) => {
      // A staged draft has no pipe to hang a rule on until it is saved.
      if (!row || row.isCreateRow || row.isRuleRow || isViewOnly) return null
      if (isDraftId(row.id)) return null

      if (creatingRuleFor === row.id) {
        return <CircularProgress size={16} sx={{ color: 'primary.main' }} />
      }

      // Icon only; the tooltip carries the words. Round outline = add, so it
      // reads as the same affordance family as the delete that replaces it.
      if (!row.flowRuleId) {
        return (
          <Tooltip title={copy.setUpTip} arrow>
            <IconButton
              size="small"
              data-no-cell-edit
              onClick={() => handleCreateRule(row)}
              // Literal greens, not primary.main: that token is #004744 in
              // light but #FFFFFF in dark, so a white icon on it produced a
              // blank white square on every rule-less pipe. Tint + outline +
              // coloured icon, the same construction as the delete button
              // this one is replaced by.
              sx={{
                width: 30,
                height: 30,
                flexShrink: 0,
                color: isDark ? '#34d399' : '#0f9d58',
                border: '1px solid',
                borderColor: isDark
                  ? 'rgba(52, 211, 153, 0.45)'
                  : 'rgba(15, 157, 88, 0.45)',
                backgroundColor: isDark
                  ? 'rgba(52, 211, 153, 0.12)'
                  : 'rgba(15, 157, 88, 0.10)',
                '&:hover': {
                  backgroundColor: isDark
                    ? 'rgba(52, 211, 153, 0.22)'
                    : 'rgba(15, 157, 88, 0.18)',
                },
              }}
            >
              <CharacterPlus sx={{ fontSize: 15, color: 'inherit' }} />
            </IconButton>
          </Tooltip>
        )
      }

      return (
        <Tooltip title={copy.removeTip} arrow>
          <IconButton
            size="small"
            data-no-cell-edit
            onClick={() => handleRequestDeleteRule(row)}
            sx={{
              width: 30,
              height: 30,
              flexShrink: 0,
              color: DANGER_TEXT,
              border: '1px solid',
              borderColor: 'rgba(220, 53, 69, 0.45)',
              backgroundColor: 'rgba(220, 53, 69, 0.08)',
              '&:hover': { backgroundColor: 'rgba(220, 53, 69, 0.18)' },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      )
    },
    [
      copy,
      isDark,
      isViewOnly,
      creatingRuleFor,
      handleCreateRule,
      handleRequestDeleteRule,
    ],
  )

  /**
   * The rule's four workspaces as one strip of small labelled chips: icon,
   * one word, and the count where there is one. Bare icons proved too
   * cryptic; four fully-labelled columns spent ~540px mostly on headers.
   */
  const RuleConfigCell = useCallback(
    ({ row }: GridCellCtx<HydraulicTreeRow>) => {
      if (!row || row.isCreateRow || row.isRuleRow || !row.flowRuleId)
        return null

      const alarmOn = ruleActiveMap?.[String(row.flowRuleId)] !== false
      const valvesFaulted = rulesWithValveError.has(Number(row.flowRuleId))
      const sectionActive = (section: string) =>
        sectionPopover?.pipeId === row.id && sectionPopover?.section === section

      const chip = (
        key: string,
        label: string,
        tooltip: string,
        icon: React.ReactNode,
        onClick: (event: React.MouseEvent) => void,
        options?: {
          color?: string
          active?: boolean
          count?: number
          /** Paints the count badge red — something in there is faulted. */
          countTone?: 'error'
        },
      ) => (
        <Tooltip key={key} title={tooltip} arrow>
          <Stack
            component="button"
            direction="row"
            data-no-cell-edit
            onClick={onClick}
            sx={{
              alignItems: 'center',
              gap: '4px',
              height: '26px',
              px: '7px',
              flexShrink: 0,
              borderRadius: '13px',
              border: '1px solid',
              borderColor: options?.active
                ? 'rgba(0, 71, 68, 0.5)'
                : 'border.main',
              backgroundColor: options?.active
                ? 'rgba(0, 71, 68, 0.12)'
                : 'background.default',
              color: options?.color ?? 'grey.800',
              cursor: 'pointer',
              font: 'inherit',
              '&:hover': {
                backgroundColor: 'rgba(0, 71, 68, 0.08)',
                borderColor: 'rgba(0, 71, 68, 0.4)',
              },
            }}
          >
            {icon}
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 650,
                color: 'inherit',
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </Typography>
            {options?.count !== undefined && (
              <Typography
                component="span"
                sx={{
                  minWidth: '15px',
                  height: '15px',
                  px: '3px',
                  borderRadius: '8px',
                  backgroundColor:
                    options.countTone === 'error'
                      ? DANGER_TEXT
                      : options.count > 0
                        ? '#2f7d54'
                        // The empty badge carries white text too, and #aeb4bb
                        // put it at 2.09:1 — a "0" you cannot read.
                        : isDark
                          ? '#5b6672'
                          : '#6b7280',
                  color: '#ffffff',
                  fontSize: '9.5px',
                  fontWeight: 700,
                  lineHeight: '15px',
                  textAlign: 'center',
                }}
              >
                {options.count}
              </Typography>
            )}
          </Stack>
        </Tooltip>
      )

      // #2f7d54 only reaches 3.34:1 on the dark row, so dark gets the same
      // emerald the valve cards and the alarm panel's bands use.
      const chipOn = isDark ? '#4ade80' : '#2f7d54'
      const chipOff = isDark ? '#9aa6b0' : '#8a9099'

      return (
        <Stack direction="row" sx={{ gap: '5px', alignItems: 'center' }}>
          {chip(
            'alarm',
            t('hydraulicChipAlarm'),
            `${copy.flowAlarm} — ${
              alarmOn ? copy.activatedChip : copy.disabledChip
            }`,
            alarmOn ? (
              <NotificationsActiveOutlinedIcon sx={{ fontSize: 14 }} />
            ) : (
              <NotificationsOffOutlinedIcon sx={{ fontSize: 14 }} />
            ),
            (event) => handleOpenAlarm(row, event.currentTarget as HTMLElement),
            { color: alarmOn ? chipOn : chipOff },
          )}
          {chip(
            'learning',
            t('learnFlow'),
            copy.learning,
            <SchoolOutlinedIcon sx={{ fontSize: 14 }} />,
            (event) =>
              handleOpenSection(
                row,
                'learning',
                event.currentTarget as HTMLElement,
              ),
            { active: sectionActive('learning') },
          )}
          {chip(
            'sensors',
            t('hydraulicColumnSensors'),
            `${copy.sensors} (${Number(row.sensors ?? 0)})`,
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                '& svg': { width: 15, height: 15 },
              }}
            >
              <SensorGlyph />
            </Box>,
            (event) =>
              handleOpenSection(
                row,
                'sensors',
                event.currentTarget as HTMLElement,
              ),
            {
              active: sectionActive('sensors'),
              count: Number(row.sensors ?? 0),
            },
          )}
          {chip(
            'valves',
            copy.valves,
            valvesFaulted
              ? t('hydraulicChipValvesErrorTooltip', {
                  label: copy.valves,
                  count: Number(row.valves ?? 0),
                })
              : `${copy.valves} (${Number(row.valves ?? 0)})`,
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                '& svg': { width: 15, height: 15 },
              }}
            >
              <ValveGlyph />
            </Box>,
            (event) =>
              handleOpenSection(
                row,
                'valves',
                event.currentTarget as HTMLElement,
              ),
            {
              active: sectionActive('valves'),
              count: Number(row.valves ?? 0),
              countTone: valvesFaulted ? 'error' : undefined,
            },
          )}
        </Stack>
      )
    },
    [
      copy,
      isDark,
      ruleActiveMap,
      sectionPopover,
      handleOpenAlarm,
      handleOpenSection,
      rulesWithValveError,
      t,
    ],
  )

  const FertigationCell = useCallback(
    ({ row }: GridCellCtx<HydraulicTreeRow>) => {
      // A staged draft has no route to link to until it is saved.
      if (!row || row.isCreateRow || isDraftId(row.id)) return null
      return (
        <RenderIf isShow={!row.parentId}>
          <Link
            to={CONFIG_ROUTES.fertigation.to.replace(
              ':pipeId',
              row.id.toString(),
            )}
            style={{ textDecoration: 'none' }}
            data-no-cell-edit
          >
            <span style={brandLinkChip(isDark)}>
              <Typography
                sx={{ fontSize: '13px', fontWeight: 500, color: 'inherit' }}
              >
                {t('actionManage')}
              </Typography>
              <EditTwoIcon sx={{ color: 'inherit', fontSize: 16 }} />
            </span>
          </Link>
        </RenderIf>
      )
    },
    [isDark, t],
  )

  const flowValueOf = useCallback(
    (row: HydraulicTreeRow, field: string) => {
      const value = (row as any)[field]
      if (value === undefined || value === null) return null
      if (value === 0) return row.flowRuleId ? 0 : ''
      return convertFromMetricV2(value, 'l/m', 'gpm', 2)
    },
    [convertFromMetricV2],
  )

  const StateCell = useCallback(
    ({ row }: GridCellCtx<HydraulicTreeRow>) => {
      if (!row || row.isCreateRow || row.isRuleRow) return null
      if (!row.status) return null

      const meta = flowRuleStateMeta(row.status)
      // Learn Flow and Verify Flow are modes, not faults — the rule is
      // deliberately measuring. Painting them red said the opposite.
      const stateColor = {
        ok: isDark ? '#34d399' : '#2e7d32',
        mode: isDark ? '#93c5fd' : '#1d4ed8',
        alarm: isDark ? '#f87171' : '#d32f2f',
        config: isDark ? '#fbbf24' : '#b45309',
      }[meta.kind]

      const isClearing = clearingIds.has(row.id)
      const hasReturned = returnedIds.has(row.id)
      const noValve = meta.clearable && !canAcknowledge(row)

      return (
        <Stack
          direction="row"
          alignItems="center"
          gap="5px"
          sx={{ minWidth: 0 }}
        >
          <Tooltip
            title={meta.descriptionKey ? t(meta.descriptionKey) : ''}
            placement="top"
          >
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 700,
                color: stateColor,
                whiteSpace: 'nowrap',
                cursor: meta.descriptionKey ? 'help' : 'default',
              }}
            >
              {row.status}
            </Typography>
          </Tooltip>

          {/* A mode ends by itself, so it gets a quiet progress hint rather
              than anything to act on. */}
          {meta.kind === 'mode' && (
            <CircularProgress size={11} sx={{ color: stateColor }} />
          )}

          {/* Resource Missing cannot be cleared at all — the rule points at an
              input or output that is not there. Offer the fix instead. */}
          {meta.kind === 'config' && (
            <Tooltip title={t(CLEAR_COPY.checkIo)} placement="top">
              <Box
                component="button"
                type="button"
                data-no-cell-edit
                onClick={(event: React.MouseEvent) =>
                  handleOpenSection(
                    row,
                    'sensors',
                    event.currentTarget as HTMLElement,
                  )
                }
                sx={{
                  border: 'none',
                  background: 'none',
                  p: 0,
                  font: 'inherit',
                  fontSize: '11px',
                  fontWeight: 650,
                  color: stateColor,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {t(CLEAR_COPY.checkIo)}
              </Box>
            </Tooltip>
          )}

          {meta.clearable && (
            <Tooltip
              title={
                isClearing
                  ? t(CLEAR_COPY.clearing)
                  : noValve
                    ? t(ACK_NO_VALVE_HINT_KEY)
                    : t(CLEAR_COPY.clear)
              }
              placement="top"
            >
              <span data-no-cell-edit>
                <IconButton
                  size="small"
                  onClick={() => handleAcknowledge(row)}
                  disabled={isViewOnly || isClearing || noValve}
                  sx={{
                    padding: '2px',
                    opacity: isViewOnly || noValve ? 0.2 : 1,
                    cursor: isViewOnly ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isClearing ? (
                    <CircularProgress size={14} sx={{ color: '#EF4136' }} />
                  ) : (
                    <SyncErrorIcon
                      sx={{ color: '#EF4136', fontSize: '16px' }}
                    />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* The whole point of the exercise: a clear that did not stick says
              so, instead of the row quietly going back to red. */}
          {hasReturned && !isClearing && (
            <Tooltip title={t(CLEAR_COPY.returned)} placement="top">
              <Typography
                data-no-cell-edit
                onClick={() => dismissReturned(row.id)}
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  lineHeight: 1,
                  px: '5px',
                  py: '2px',
                  borderRadius: '7px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  color: isDark ? '#fbbf24' : '#b45309',
                  backgroundColor: isDark
                    ? 'rgba(251, 191, 36, 0.16)'
                    : 'rgba(180, 83, 9, 0.1)',
                }}
              >
                {t('hydraulicBadgeCameBack')}
              </Typography>
            </Tooltip>
          )}
        </Stack>
      )
    },
    [
      t,
      isDark,
      isViewOnly,
      handleAcknowledge,
      canAcknowledge,
      handleOpenSection,
      clearingIds,
      returnedIds,
      dismissReturned,
    ],
  )

  const StateHeader = useCallback(
    () => (
      <Stack direction="row" alignItems="center" gap="6px" sx={{ flex: 1 }}>
        <span>{t('hydraulicColumnState')}</span>
        <HydraulicAckButton />
      </Stack>
    ),
    [t],
  )

  const ActionCell = useCallback(
    ({ row }: GridCellCtx<HydraulicTreeRow>) => {
      if (!row) return null

      // The "+ Add …" chip row is an affordance, not a record.
      if (row.isCreateRow) return null

      // Bulk edit: DELETE only. Adding a sub-pipe is started from the strip
      // that sits under the pipe itself, where the new row will appear.
      //
      // A draft gets the same ✕ as a saved row, so one staged pipe can be
      // dropped without cancelling the whole batch. It has never been
      // written, so it is discarded locally instead of going through the
      // delete modal.
      if (bulkEditMode) {
        const isDraft = isDraftId(row.id)
        return (
          <Stack
            direction="row"
            data-no-cell-edit
            sx={{
              height: '100%',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DeleteIconButton
              disabled={isViewOnly}
              isDark={isDark}
              onClick={() => {
                if (isViewOnly) return
                if (isDraft) handleDiscardDraft(row.id)
                else
                  handleDelete(
                    row.id,
                    (row as any).interfaceId,
                    (row as any).flowRuleId,
                    row.description,
                  )
              }}
            />
          </Stack>
        )
      }

      const actionStyle = {
        fontSize: '13px',
        fontWeight: '500',
        lineHeight: '100%',
        color: isViewOnly ? 'black' : 'primary.main',
        cursor: isViewOnly ? 'not-allowed' : 'pointer',
        opacity: isViewOnly ? 0.2 : 1,
      }

      return (
        <Stack direction="row" spacing="20px" data-no-cell-edit>
          {Number(row.level) !== MAX_PIPE_LEVEL && (
            <Typography
              sx={{ ...actionStyle, display: 'flex', width: '85px' }}
              onClick={() => !isViewOnly && handleAddSubPipe(row.id.toString())}
            >
              {t('actionAddSubPipe')}
            </Typography>
          )}
          <Typography
            sx={actionStyle}
            onClick={() =>
              !isViewOnly && handleEdit(row.id.toString(), String(row.parentId))
            }
          >
            {t('actionEdit')}
          </Typography>
          <Typography
            sx={{ ...actionStyle, color: isViewOnly ? 'black' : 'grey.600' }}
            onClick={() =>
              !isViewOnly &&
              handleDelete(
                row.id,
                (row as any).interfaceId,
                (row as any).flowRuleId,
                row.description,
              )
            }
          >
            {t('actionDelete')}
          </Typography>
        </Stack>
      )
    },
    [
      bulkEditMode,
      isViewOnly,
      isDark,
      t,
      handleAddSubPipe,
      handleEdit,
      handleDelete,
      handleDiscardDraft,
    ],
  )

  const DescriptionCell = useCallback(
    ({ row, value }: GridCellCtx<HydraulicTreeRow>) => {
      if (!row) return null

      // The chip stays put once a draft has been added, so several mainlines
      // can be staged one after another.
      if (row.isCreateRow) {
        return (
          <AddRowChip
            label={`+ ${t('commonActionAddNewMainline')}`}
            disabled={isViewOnly}
            isDark={isDark}
            onClick={() => addDraft(null)}
          />
        )
      }

      // Normal row: the editable highlight is on the whole cell (cellStyle).
      // The hover strip that drops a sub-pipe at the row boundary is NOT
      // rendered here — see the overlay. What IS here is a always-visible
      // affordance, because a hover-only control gives no clue it exists.
      const canTakeChild =
        bulkEditMode && !isViewOnly && Number(row.level) < MAX_PIPE_LEVEL

      return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          <Box
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              // Keeps a long name from running under the button.
              paddingRight: canTakeChild ? '22px' : 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {(value as React.ReactNode) ?? null}
          </Box>

          {canTakeChild && (
            <Tooltip title={t('actionAddSubPipe')} arrow placement="top">
              <Box
                component="button"
                type="button"
                data-no-cell-edit
                aria-label={t('actionAddSubPipe')}
                onClick={(event: React.MouseEvent) => {
                  // The cell would otherwise open its editor behind the button.
                  event.stopPropagation()
                  addDraft({
                    id: row.id,
                    level: Number(row.level) || 1,
                    interfaceId: (row as any).interfaceId,
                  })
                }}
                sx={{
                  position: 'absolute',
                  // Negative offset reaches into the cell's own 12px padding
                  // so the button sits near the true edge; `overflow: hidden`
                  // on the cell clips at the border box, not here.
                  right: '-7px',
                  bottom: '4px',
                  width: '17px',
                  height: '17px',
                  padding: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  lineHeight: 1,
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: isDark ? '#5fe0ad' : '#004744',
                  backgroundColor: isDark
                    ? 'rgba(95, 224, 173, 0.12)'
                    : 'rgba(0, 71, 68, 0.08)',
                  border: `1px solid ${
                    isDark
                      ? 'rgba(95, 224, 173, 0.45)'
                      : 'rgba(0, 71, 68, 0.35)'
                  }`,
                  transition: 'background-color 120ms ease',
                  '&:hover': {
                    backgroundColor: isDark
                      ? 'rgba(95, 224, 173, 0.24)'
                      : 'rgba(0, 71, 68, 0.16)',
                  },
                }}
              >
                +
              </Box>
            </Tooltip>
          )}
        </Box>
      )
    },
    [isDark, isViewOnly, t, addDraft, bulkEditMode],
  )

  /**
   * One cell for the whole flow rule: the add/remove control that governs the
   * rule, then the four workspaces it opens. The control leads and is drawn
   * heavier than the chips — it is what creates the rule the chips belong to,
   * and removing it takes them with it.
   */
  const FlowRuleCell = useCallback(
    (ctx: GridCellCtx<HydraulicTreeRow>) => {
      const { row } = ctx
      if (!row || row.isCreateRow || row.isRuleRow) return null

      return (
        <Stack
          direction="row"
          sx={{ alignItems: 'center', gap: '10px', height: '100%' }}
        >
          <RuleCell {...ctx} />
          {row.flowRuleId ? (
            <>
              <Box
                sx={{
                  width: '1px',
                  height: '18px',
                  flexShrink: 0,
                  backgroundColor: 'border.main',
                }}
              />
              <RuleConfigCell {...ctx} />
            </>
          ) : null}
        </Stack>
      )
    },
    [RuleCell, RuleConfigCell],
  )

  const CapacityCell = useCallback(
    ({ row, value }: GridCellCtx<HydraulicTreeRow>) => {
      if (!row || row.isCreateRow) return null
      // The column's valueGetter has already converted, so this renders what
      // it is handed. Drafts are no longer a special case: they hold metric
      // like every other row, so they read the same as a saved one.
      return <>{(value as React.ReactNode) ?? ''}</>
    },
    [],
  )

  return {
    ActionCell,
    CapacityCell,
    DescriptionCell,
    FertigationCell,
    FlowRuleCell,
    StateCell,
    StateHeader,
    flowValueOf,
  }
}

export default useCellRenderers
